import React from 'react'
import { StyleSheet, TouchableOpacity, ViewStyle, StyleProp, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Icon } from '../atoms'
import { Theme } from '@theme'
import { Text } from '../atoms'
import { SOCIAL_LOGIN_ENABLED } from '../../utils/featureFlags'

export type SocialProvider = 'facebook' | 'linkedin' | 'google' | 'apple'

interface ButtonSocialProps {
  provider: SocialProvider
  onPress: () => void
  style?: StyleProp<ViewStyle>
}

interface ButtonSocialGroupProps {
  onPressFacebook?: () => void
  onPressLinkedin?: () => void
  onPressGoogle?: () => void
  onPressApple?: () => void
  style?: StyleProp<ViewStyle>
  title?: string
}

const getIconName = (provider: SocialProvider) => {
  switch (provider) {
    case 'facebook':
      return 'facebook'
    case 'linkedin':
      return 'linkedin'
    case 'google':
      return 'google'
    case 'apple':
      return 'apple'
  }
}

const getIconColor = (provider: SocialProvider) => {
  switch (provider) {
    case 'facebook':
      return '#1877F2'
    case 'linkedin':
      return '#0A66C2'
    case 'google':
      return '#4285F4'
    case 'apple':
      return '#000000'
  }
}

const Divider = ({ style }: { style?: StyleProp<ViewStyle> }) => {
  const theme = useTheme()
  return <View style={[{ flex: 1, height: 1, backgroundColor: theme.colors.primary }, style]} />
}

export const ButtonSocial = ({ provider, onPress, style }: ButtonSocialProps): React.ReactElement => {
  const theme = useTheme()
  const styles = getStyles(theme)

  return (
    <TouchableOpacity
      style={[styles.container, style]}
      onPress={onPress}
      activeOpacity={0.8}
    >
      <Icon
        name={getIconName(provider)}
        size={24}
        color={getIconColor(provider)}
      />
    </TouchableOpacity>
  )
}

export const ButtonSocialGroup = ({
  onPressFacebook,
  onPressLinkedin,
  onPressGoogle,
  onPressApple,
  style,
  title,
}: ButtonSocialGroupProps): React.ReactElement | null => {
  const theme = useTheme()
  const styles = getStyles(theme)

  // Each provider shows only when its handler is passed AND its feature flag is
  // on. Flags live in utils/featureFlags and are toggleable via an OTA update.
  const showFacebook = Boolean(onPressFacebook) && SOCIAL_LOGIN_ENABLED.facebook
  const showLinkedin = Boolean(onPressLinkedin) && SOCIAL_LOGIN_ENABLED.linkedin
  const showGoogle = Boolean(onPressGoogle) && SOCIAL_LOGIN_ENABLED.google
  const showApple = Boolean(onPressApple) && SOCIAL_LOGIN_ENABLED.apple

  // Hide the whole block (including the "or" divider) when no provider is enabled.
  if (!showFacebook && !showLinkedin && !showGoogle && !showApple) {
    return null
  }

  return (
    <View style={[styles.groupContainer, style]}>
      {title && (
        <View style={styles.titleContainer}>
          <Divider style={styles.divider} />
          <Text type="bodySmall" color={theme.colors.lightGrey} style={styles.title}>
            {title}
          </Text>
          <Divider style={styles.divider} />
        </View>
      )}
      <View style={styles.buttonsContainer}>
        {showFacebook && onPressFacebook && <ButtonSocial provider="facebook" onPress={onPressFacebook} />}
        {showLinkedin && onPressLinkedin && <ButtonSocial provider="linkedin" onPress={onPressLinkedin} />}
        {showGoogle && onPressGoogle && <ButtonSocial provider="google" onPress={onPressGoogle} />}
        {showApple && onPressApple && <ButtonSocial provider="apple" onPress={onPressApple} />}
      </View>
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      width: 48,
      height: 48,
      borderRadius: 24,
      backgroundColor: theme.colors.white,
      justifyContent: 'center',
      alignItems: 'center',
      shadowColor: theme.colors.black,
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowRadius: 4,
    },
    groupContainer: {
      width: '100%',
      alignItems: 'center',
    },
    buttonsContainer: {
      flexDirection: 'row',
      justifyContent: 'center',
      gap: theme.spacing.md,
    },
    titleContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      width: '100%',
      marginBottom: theme.spacing.xl,
    },
    title: {
      marginHorizontal: theme.spacing.md,
      textAlign: 'center',
    },
    divider: {
      marginHorizontal: theme.spacing.md,
    },
  })
